import React, { useContext, useEffect } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { NoAccessibleData } from '../components/PermissionDenied';
import {
  getUserRole,
  canCreateCrews,
  canEditCrews,
  getCoachingTrainingGroups,
  getAllUserTrainingGroupIds,
} from '../utils/roles';
import { loadBoatTypes } from '../utils/boatTypes';

// Hooks
import { useCrewsData, useCrewOperations, useDragAndDrop } from '../hooks';
import { useMobile } from '../hooks/useMobile';

// Components
import {
  CrewsList,
  LineupEditor,
  AvailableRowers,
  UnassignedMembers,
  CreateCrewModal,
  EditCrewModal,
} from '../components/crews';
import LoadingSpinner from '../components/LoadingSpinner';

const Crews = () => {
  const { user } = useContext(AuthContext);
  const { isMobile } = useMobile();

  // Data management
  const {
    crews,
    setCrews,
    availableRowers,
    trainingGroups,
    loading,
    loadData,
    refetchCrews,
    selectedCrew,
    setSelectedCrew,
  } = useCrewsData();

  // Load boat types on component mount
  useEffect(() => {
    loadBoatTypes();
  }, []);

  // Crew operations
  const {
    isCreateModalOpen,
    setIsCreateModalOpen,
    isEditModalOpen,
    setIsEditModalOpen,
    editingCrew,
    setEditingCrew,
    newCrew,
    setNewCrew,
    handleCreateCrew,
    handleDeleteCrew,
    handleEditCrew,
    handleUpdateCrew,
  } = useCrewOperations(crews, setCrews, selectedCrew, setSelectedCrew);

  // Drag and drop
  const {
    dropTarget,
    handleDragStart,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleRemoveFromPosition,
  } = useDragAndDrop(selectedCrew, setSelectedCrew, crews, setCrews);

  if (loading) return <LoadingSpinner />;

  const userCanCreate = canCreateCrews(user);
  const userCanEdit = canEditCrews(user);
  const coachingGroupIds = getCoachingTrainingGroups(user);
  const userGroupIds = getAllUserTrainingGroupIds(user);

  // All users see crews from their training groups
  const visibleCrews = crews.filter(
    (c) => c.training_group_id && userGroupIds.includes(c.training_group_id)
  );

  // For crew creation, only show training groups where the user is a coach
  let coachingTrainingGroups;
  if (!userCanCreate) {
    coachingTrainingGroups = trainingGroups.filter((g) => coachingGroupIds.includes(g.id));
  } else {
    coachingTrainingGroups = trainingGroups;
  }

  const openCreateModal = () => {
    // Preselect first coaching group for coaches to avoid backend permission errors
    const defaultGroupId = coachingGroupIds[0] || null;
    setNewCrew({ ...newCrew, training_group_id: defaultGroupId });
    setIsCreateModalOpen(true);
  };

  // Only show "no data" when the user cannot create crews
  if (crews.length === 0 && !userCanCreate) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8">
            <h1 className="mb-2 text-3xl font-bold text-gray-900">Crew Management</h1>
            <p className="text-gray-600">
              Create and manage rowing crew lineups with drag-and-drop
            </p>
          </div>
          <NoAccessibleData dataType="crews" userRole={getUserRole(user)} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">Crew Management</h1>
          <p className="text-gray-600">
            Create and manage rowing crew lineups with{' '}
            {isMobile ? 'dropdown selections' : 'drag-and-drop'}
          </p>
        </div>

        {/* Mobile Layout: 2 columns */}
        {isMobile ? (
          <div className="grid h-[calc(100vh-200px)] grid-cols-1 gap-6 md:grid-cols-2">
            <CrewsList
              crews={visibleCrews}
              selectedCrew={selectedCrew}
              onCrewSelect={setSelectedCrew}
              onCreateCrew={openCreateModal}
              onEditCrew={handleEditCrew}
              onDeleteCrew={handleDeleteCrew}
              canCreate={userCanCreate}
              canEdit={userCanEdit}
            />

            <LineupEditor
              selectedCrew={selectedCrew}
              setSelectedCrew={setSelectedCrew}
              crews={crews}
              setCrews={setCrews}
              availableRowers={availableRowers}
            />
          </div>
        ) : (
          /* Desktop Layout: 3 columns with AvailableRowers */
          <div className="grid h-[calc(100vh-200px)] grid-cols-1 gap-6 lg:grid-cols-3">
            <CrewsList
              crews={visibleCrews}
              selectedCrew={selectedCrew}
              onCrewSelect={setSelectedCrew}
              onCreateCrew={openCreateModal}
              onEditCrew={handleEditCrew}
              onDeleteCrew={handleDeleteCrew}
              canCreate={userCanCreate}
              canEdit={userCanEdit}
            />

            <LineupEditor
              selectedCrew={selectedCrew}
              setSelectedCrew={setSelectedCrew}
              crews={crews}
              setCrews={setCrews}
              availableRowers={availableRowers}
            />

            <div className="flex h-full flex-col">
              <UnassignedMembers
                selectedCrew={selectedCrew}
                onDragStart={handleDragStart}
                onCrewUpdate={refetchCrews}
              />
              <AvailableRowers
                availableRowers={availableRowers}
                trainingGroups={trainingGroups}
                selectedCrew={selectedCrew}
                onDragStart={handleDragStart}
              />
            </div>
          </div>
        )}

        <CreateCrewModal
          isOpen={isCreateModalOpen}
          newCrew={newCrew}
          trainingGroups={coachingTrainingGroups}
          onClose={() => setIsCreateModalOpen(false)}
          onCrewChange={setNewCrew}
          onSubmit={handleCreateCrew}
        />

        <EditCrewModal
          isOpen={isEditModalOpen}
          crew={editingCrew}
          trainingGroups={coachingTrainingGroups}
          onClose={() => setIsEditModalOpen(false)}
          onCrewChange={setEditingCrew}
          onSubmit={handleUpdateCrew}
        />
      </div>
    </div>
  );
};

export default Crews;
